import { YoutubeAnalyticsOverviewPayload } from '@anytag/graphql';
import { RenderDataWithFallback } from '@anytag/shared-atoms';
import { DAILY_POST_PARAM_KEY } from '@anytag/shared-constants';
import { useSelectItemIds } from '@anytag/shared-hooks';
import { YoutubePostInDateDetailsModal } from '@anytag/shared-molecules';
import { NoteAggregateLastThirtyDays } from '@anytag/shared-organisms';
import {
  AnalyticsOverviewChart,
  AnalyticsOverviewLegend,
  AnalyticsOverviewWrapper,
  AnalyticsOverviewEngagement
} from '../../../shared';
import { AnalyticsSelectedAccountInfo } from '../../../types';
import {
  useAnalyticsYoutubeDashboardOverviewData,
  useAnalyticsYoutubeDashboardOverviewPosts,
  useAnalyticsYoutubeDashboardOverviewLegend
} from './hooks';
import { AnalyticsYoutubeDashboardGraphKeys as Keys } from './types';
import { allOverviewEngagements } from './utils';

interface TemplateProps {
  data: YoutubeAnalyticsOverviewPayload;
  accountId: AnalyticsSelectedAccountInfo['id'];
  isInfluencerProfile?: boolean;
}

const Template = ({ data, accountId, isInfluencerProfile }: TemplateProps) => {
  const { availableLegendOptions } = useAnalyticsYoutubeDashboardOverviewLegend();
  const { selectItem: setSelectedLegendKey, selectedItemIds: selectedLegendKeys } = useSelectItemIds<Keys>({
    defaultSelectedIds: ['subscriber', 'post']
  });
  const { chartData, categories, overviewEngagements, availableOverviewEngagements } =
    useAnalyticsYoutubeDashboardOverviewData({
      data,
      selectedLegendKeys,
      availableEngagementKeys: allOverviewEngagements.map((item) => item.key)
    });
  const { dailyPosts, customChartOptions } = useAnalyticsYoutubeDashboardOverviewPosts({
    chartData,
    accountId,
    isInfluencerProfile
  });

  const hasNoData = !overviewEngagements.some((item) => !!item.total);

  return (
    <>
      <AnalyticsOverviewWrapper
        help=""
        dataNotAvailable={hasNoData}
        headerChildren={!hasNoData && isInfluencerProfile ? <NoteAggregateLastThirtyDays /> : null}
      >
        <AnalyticsOverviewEngagement engagement={availableOverviewEngagements} />
        <AnalyticsOverviewLegend
          options={availableLegendOptions}
          selectedOptions={selectedLegendKeys}
          onSelectOption={({ value }) => setSelectedLegendKey(value)}
        />
        <AnalyticsOverviewChart
          data={chartData}
          categories={categories}
          customTooltip={{ itemsPerRow: 3 }}
          customOptions={customChartOptions}
        />
      </AnalyticsOverviewWrapper>

      <YoutubePostInDateDetailsModal data={dailyPosts} paramKey={DAILY_POST_PARAM_KEY} />
    </>
  );
};

export interface AnalyticsYoutubeDashboardOverviewTemplateProps
  extends Pick<TemplateProps, 'accountId' | 'isInfluencerProfile'> {
  loading?: boolean;
  data?: YoutubeAnalyticsOverviewPayload | null;
}
export const AnalyticsYoutubeDashboardOverviewTemplate = ({
  data,
  loading,
  ...restProps
}: AnalyticsYoutubeDashboardOverviewTemplateProps) => (
  <RenderDataWithFallback loading={loading} skipDelayed>
    {data ? <Template {...restProps} data={data} /> : null}
  </RenderDataWithFallback>
);
